import { useCallback, useEffect, useMemo, useState } from 'react'
import type { CrmLeadCardInFunnelData } from '@/api/types/crm/crmFunnels.types'
import type { TrafficChannelShort } from '@/api/types/crm/crm.types'
import type { LeadProfileLead } from '../LeadProfileModal.types'

export function useLeadProfileTrafficChannel(params: {
  lead: LeadProfileLead | undefined
  multipleDealsEnabled: boolean
  selectedFunnelId: number | null
  selectedLeadFunnelId: number | null
  cardFunnelSnapshotEpoch: number
  getCardInFunnelSnapshot: (
    funnelId: number,
    leadFunnelId?: number | null,
  ) => CrmLeadCardInFunnelData | undefined
}) {
  const {
    lead,
    multipleDealsEnabled,
    selectedFunnelId,
    selectedLeadFunnelId,
    cardFunnelSnapshotEpoch,
    getCardInFunnelSnapshot,
  } = params

  const [trafficChannelDraftId, setTrafficChannelDraftId] = useState<
    number | null
  >(null)

  useEffect(() => {
    if (!lead) return

    if (multipleDealsEnabled && selectedFunnelId != null) {
      const snap = getCardInFunnelSnapshot(selectedFunnelId, selectedLeadFunnelId)
      const fromDeal =
        snap?.traffic_channel_id ?? snap?.traffic_channel?.id ?? null
      const fromDealValid =
        fromDeal != null && Number.isFinite(Number(fromDeal))
          ? Number(fromDeal)
          : null
      setTrafficChannelDraftId(
        fromDealValid ?? lead.traffic_channel?.id ?? null,
      )
      return
    }

    setTrafficChannelDraftId(lead.traffic_channel?.id ?? null)
  }, [
    lead?.id,
    lead?.traffic_channel?.id,
    multipleDealsEnabled,
    selectedFunnelId,
    selectedLeadFunnelId,
    cardFunnelSnapshotEpoch,
    getCardInFunnelSnapshot,
    lead,
  ])

  const trafficChannelCurrent = useMemo((): TrafficChannelShort | null => {
    if (multipleDealsEnabled && selectedFunnelId != null) {
      const snap = getCardInFunnelSnapshot(selectedFunnelId, selectedLeadFunnelId)
      if (snap?.traffic_channel) return snap.traffic_channel
    }
    return lead?.traffic_channel ?? null
  }, [
    multipleDealsEnabled,
    selectedFunnelId,
    selectedLeadFunnelId,
    cardFunnelSnapshotEpoch,
    getCardInFunnelSnapshot,
    lead?.traffic_channel,
  ])

  const resetTrafficChannelDraft = useCallback(() => {
    if (!lead) return

    if (multipleDealsEnabled && selectedFunnelId != null) {
      const snap = getCardInFunnelSnapshot(selectedFunnelId, selectedLeadFunnelId)
      const fromDeal =
        snap?.traffic_channel_id ?? snap?.traffic_channel?.id ?? null
      const fromDealValid =
        fromDeal != null && Number.isFinite(Number(fromDeal))
          ? Number(fromDeal)
          : null
      setTrafficChannelDraftId(
        fromDealValid ?? lead.traffic_channel?.id ?? null,
      )
      return
    }

    setTrafficChannelDraftId(lead.traffic_channel?.id ?? null)
  }, [
    getCardInFunnelSnapshot,
    lead,
    multipleDealsEnabled,
    selectedFunnelId,
    selectedLeadFunnelId,
  ])

  return {
    trafficChannelDraftId,
    setTrafficChannelDraftId,
    trafficChannelCurrent,
    resetTrafficChannelDraft,
  }
}
